import { applyLabWeightOverride } from './labMeasurement';
import { hasManualCompoundField, manualCompoundField, manualCompoundFieldValue, manualCompoundSource, manualOverrideState } from './manualLabOverrides';

/** Normalize a cannabinoid key or slug to bare lowercase alphanumeric for comparison. */
export const normalizeCbSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Merge cannabinoid or terpene overrides back onto a compound map (ingredients / terpenes). */
export const applyCompoundOverrides = (
  target: Record<string, any>,
  overridesSrc: Record<string, any>,
  prefix: string,
  normalizer: (s: string) => string,
  canonicalList: readonly string[],
  overrideKeyFn: (name: string) => { enabled: string; [k: string]: string },
  hasUom = false,
  explicitIntent: Record<string, unknown> = {},
  fallback: unknown = {},
): Record<string, any> => {
  const result = { ...target };
  const handledFamilies = new Set<string>();
  Object.keys(overridesSrc)
    .filter((k) => k.startsWith(prefix) && k.endsWith('_override'))
    .forEach((overrideKey) => {
      const slugMatch = overrideKey.match(new RegExp(`^${prefix}(.+?)_override$`));
      if (!slugMatch) return;
      const slug = slugMatch[1];
      const canonicalName = canonicalList.find(name => normalizer(name) === normalizer(slug));
      const enabledKey = canonicalName ? overrideKeyFn(canonicalName).enabled : overrideKey;
      const family = manualCompoundField(enabledKey)?.family;
      if (!family || handledFamilies.has(family)) return;
      // Hydration is not an edit: retained source keys may be literal template bindings.
      const hasRetainedAlias = canonicalName && Object.keys(target).some(key =>
        key !== canonicalName && normalizer(key) === normalizer(slug));
      if (hasRetainedAlias && manualOverrideState(overridesSrc, enabledKey) !== false
        && typeof manualOverrideState(explicitIntent, enabledKey) !== 'boolean') return;
      handledFamilies.add(family);
      const canonicalPrefix = canonicalName ? overrideKeyFn(canonicalName).enabled.replace(/_override$/, '') : undefined;
      const valueFor = (suffix: string) => {
        if (manualOverrideState(explicitIntent, enabledKey) === true) return manualCompoundFieldValue(explicitIntent, enabledKey, suffix);
        const canonicalField = canonicalPrefix ? `${canonicalPrefix}_${suffix}` : undefined;
        if (canonicalField && Object.prototype.hasOwnProperty.call(overridesSrc, canonicalField)) return overridesSrc[canonicalField];
        const alias = Object.entries(overridesSrc).find(([name]) => {
          const candidate = manualCompoundField(name);
          return candidate?.family === family && candidate.suffix === suffix.toLowerCase();
        });
        return alias?.[1];
      };
      const enabled = manualOverrideState(overridesSrc, enabledKey);
      const foundKey =
        Object.keys(result).find((k) => normalizer(k) === normalizer(slug)) ??
        canonicalList.find((c) => overrideKeyFn(c).enabled === overrideKey) ??
        slug;
      const canonicalKey = canonicalName ?? foundKey;
      if (enabled === false) {
        // A source snapshot may retain several spellings of the same hydrated compound.
        for (const key of Object.keys(result)) {
          if (normalizer(key) === normalizer(slug)) delete result[key];
        }
        delete result[canonicalKey];
      } else if (enabled === true) {
        const percentVal = valueFor('percent');
        const weightVal = hasUom && !hasManualCompoundField(explicitIntent, enabledKey, 'weightmgg') ? undefined : valueFor('weightMgG');
        const pkgVal = valueFor('weightPkg');
        const srvVal = valueFor('weightSrv');
        const uomVal = hasUom ? valueFor('uom') : undefined;
        const compounds = hasUom ? result : manualCompoundSource(result, fallback, enabledKey);
        const sourceKey = Object.prototype.hasOwnProperty.call(compounds, canonicalKey) ? canonicalKey
          : Object.keys(compounds).find(key => normalizer(key) === normalizer(slug));
        const source = sourceKey === undefined ? undefined : compounds[sourceKey];
        const existing = source && typeof source === 'object' && !Array.isArray(source) ? source : {};
        for (const key of Object.keys(result)) {
          if (key !== canonicalKey && normalizer(key) === normalizer(slug)) delete result[key];
        }
        const mlVal = hasUom ? valueFor('weightMl') : undefined;
        const perUnitVal = hasUom ? valueFor('weightPerUnit') : undefined;
        const hasWeightOverride =
          mlVal !== undefined || perUnitVal !== undefined || weightVal !== undefined ||
          pkgVal !== undefined ||
          srvVal !== undefined ||
          uomVal !== undefined;
        if (!hasUom && sourceKey !== undefined && percentVal === undefined && !hasWeightOverride) {
          result[canonicalKey] = source;
          return;
        }
        // Keep an existing legacy percentage alias aligned with explicit edits,
        // including clears and zeroes, so fallback cannot resurrect an old value.
        const hasPercentAliasOverride = percentVal !== undefined && existing.weight?.percent !== undefined;
        let nextWeight = {
          ...(existing.weight || {}),
          ...(hasPercentAliasOverride ? { percent: percentVal } : {}),
          ...(mlVal !== undefined ? { ml: mlVal } : {}),
          ...(perUnitVal !== undefined ? { perUnit: perUnitVal } : {}),
          ...(pkgVal !== undefined ? { pkg: pkgVal } : {}),
          ...(srvVal !== undefined ? { srv: srvVal } : {}),
          ...(uomVal !== undefined ? { unit: uomVal } : {}),
        };
        if (weightVal !== undefined) nextWeight = applyLabWeightOverride(nextWeight, weightVal, uomVal);
        result[canonicalKey] = {
          ...existing,
          ...(percentVal !== undefined ? { percent: percentVal } : {}),
          ...(hasWeightOverride || hasPercentAliasOverride
            ? {
                weight: nextWeight,
              }
            : {}),
        };
      }
    });
  return result;
};
